import axios from 'axios'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { getEnvConfig } from 'utils/getEnvConfig'
import { handleUnauthorizedRedirect } from './authRedirectHandler'

/**
 * Singleton token refresh manager.
 * Ensures only ONE refresh call happens at a time, even when
 * multiple parallel API requests detect an expired token simultaneously.
 *
 * Uses axios directly (not AuthApi/ApiService) to avoid circular dependencies:
 * ApiService → tokenRefreshManager → AuthApi → ApiService
 *
 * How it works:
 * - First caller triggers the actual refresh API call.
 * - All subsequent callers receive the same promise and wait for it.
 * - Once resolved, all waiting requests get the new access token.
 * - If refresh fails, all waiting requests are rejected and user is redirected to login.
 */
let refreshPromise: Promise<string> | null = null

export const refreshTokenWithMutex = (): Promise<string> => {
  // If a refresh is already in flight, return the same promise
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = executeRefresh()
  return refreshPromise
}

const executeRefresh = async (): Promise<string> => {
  try {
    const refreshToken = AuthStorageInstance.getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    // Call auth endpoint directly with axios to avoid circular dependency
    const response = await axios({
      method: 'POST',
      url: `${getEnvConfig('WEB_BASE_URL')}/auth/v1.0/auth`,
      data: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken
      },
      headers: { 'content-type': 'application/json' }
    })

    const { access_token, refresh_token, expires_in } = response.data

    // Store new tokens
    const currentTime = new Date()
    const expiresIn = currentTime.getTime() + (parseInt(expires_in) - 60) * 1000
    AuthStorageInstance.setAccessToken(access_token)
    AuthStorageInstance.setExpiresIn(expiresIn)
    AuthStorageInstance.setRefreshToken(refresh_token)

    console.log(
      '%c********* Token Refreshed Successfully (via mutex) ***********',
      'font-weight:bold;color:green;background-color:white;'
    )

    return access_token
  } catch (error) {
    console.log('Token refresh failed:', error)
    handleUnauthorizedRedirect()
    throw error
  } finally {
    // Clear the promise so future refreshes can proceed
    refreshPromise = null
  }
}
